import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Card, CardContent, CardHeader, Grid, Button } from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import Alert from '@material-ui/lab/Alert';
import Snackbar from '@material-ui/core/Snackbar';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import JoaillierEnvoiForm from '../form/JoaillierEnvoiForm';
import JoaillierMovements from '../components/JoaillierMovements';
import JoaillierRetourModule from '../components/JoaillierRetourModule';
import { ajouterJoaillier, modifierJoaillier } from '../action/AtelierAction';
import { getListeAtelier as getListeAtelierAction } from '../../../actions';
import { getTodaySqlFormat } from '../../../helpers/functions';

const initialOfContext = {
    num_sachet: null,
    ref: null,
    refDesc: null,
};

const safeNumber = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const parseNumberInput = (value) => {
    if (value === null || value === undefined) {
        return NaN;
    }

    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed === '') {
            return 0;
        }
        const normalized = trimmed.replace(',', '.');
        const parsed = Number(normalized);
        return Number.isFinite(parsed) ? parsed : NaN;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : NaN;
};

const EnvoiJoaillier = ({ listeAtelier, dispatchGetListeAtelier }) => {
    const [ofContext, setOfContext] = useState(initialOfContext);
    const [historiqueData, setHistoriqueData] = useState([]);
    const [draftMovements, setDraftMovements] = useState([]);
    const [selectedMovement, setSelectedMovement] = useState(null);
    const [alertMessage, setAlertMessage] = useState(null);
    const [alertSeverity, setAlertSeverity] = useState('info');
    const [loadingRetour, setLoadingRetour] = useState(false);

    const ofFormRef = useRef(null);
    const envoiFormRef = useRef(null);
    const retourFormRef = useRef(null);
    const skipRetourFocusRef = useRef(false);
    const shouldPopulateDraftRef = useRef(false);

    const scheduleFocusOnOf = useCallback(() => {
        setTimeout(() => {
            if (ofFormRef.current?.focusOfField) {
                ofFormRef.current.focusOfField();
            }
        }, 150);

        setTimeout(() => {
            skipRetourFocusRef.current = false;
        }, 300);
    }, []);

    const showToast = useCallback((message, severity = 'info') => {
        setAlertMessage(message);
        setAlertSeverity(severity);
    }, []);

    const closeToast = useCallback(() => setAlertMessage(null), []);

    const focusJoaillier = useCallback(() => {
        setTimeout(() => {
            if (envoiFormRef.current?.focusJoaillier) {
                envoiFormRef.current.focusJoaillier();
            }
        }, 150);
    }, []);

    const focusRetour = useCallback(() => {
        setTimeout(() => {
            if (retourFormRef.current?.focusPoidsR) {
                retourFormRef.current.focusPoidsR();
            }
        }, 150);
    }, []);

    const normalizeMovements = useCallback((rawMovements, numSachet) => {
        if (!Array.isArray(rawMovements)) {
            return [];
        }

        return rawMovements.map((item, index) => ({
            id_ligne: item.id_ligne || item.id_ouvrier || item.id || `local_${index}`,
            n_sachet: numSachet,
            id_ouvrier: item.id_ouvrier || item.id || null,
            label: item.nom_ouvrier || item.nom || 'Ouvrier',
            joaillier: item.nom_ouvrier || item.nom || 'Ouvrier',
            ref: item.ref_cv ?? item.ref ?? '',
            refDesc: item.ref ?? item.refDesc ?? '',
            poids_d: safeNumber(item.poids_d ?? item.poids),
            poids_r: safeNumber(item.poids_r),
            temps: safeNumber(item.temps),
            rendu: safeNumber(item.rendu, 0),
            pesee: safeNumber(item.pesee, 0),
            avatar: item.avatar ?? null,
        }));
    }, []);

    const applyHistorique = useCallback(
        (rawMovements, contextOverride = {}) => {
            const skipAutoActions = skipRetourFocusRef.current;
            const shouldPopulate = shouldPopulateDraftRef.current;

            console.log('🔍 applyHistorique - shouldPopulate:', shouldPopulate, 'skipAutoActions:', skipAutoActions);
            console.trace('📍 Called from:');

            const normalized = normalizeMovements(rawMovements, contextOverride.num_sachet ?? ofContext.num_sachet);
            const nextContext = {
                num_sachet: contextOverride.num_sachet ?? ofContext.num_sachet ?? null,
                ref: contextOverride.ref ?? rawMovements?.[0]?.ref_cv ?? ofContext.ref ?? null,
                refDesc: contextOverride.refDesc ?? rawMovements?.[0]?.ref ?? ofContext.refDesc ?? null,
            };

            setOfContext(nextContext);
            setHistoriqueData(normalized);
            const pendingList = normalized.filter((movement) => Number(movement.rendu) === 0);
            
            // Si shouldPopulate = true, AJOUTER le dernier mouvement à SAISIES (historique session)
            if (shouldPopulate && normalized.length > 0) {
                // Prendre le dernier mouvement (le plus récent)
                const latestMovement = normalized[normalized.length - 1];
                console.log('➕ Ajout à SAISIES:', latestMovement);
                setDraftMovements((prev) => [...prev, latestMovement]);
                shouldPopulateDraftRef.current = false;
            }

            // Si on skip les actions auto (après submit), on ne sélectionne rien et on focus sur OF
            if (skipAutoActions) {
                setSelectedMovement(null);
                scheduleFocusOnOf();
                return;
            }

            // Logique normale: si pas de shouldPopulate (recherche OF)
            if (!shouldPopulate) {
                const hasPending = pendingList.length > 0;
                if (hasPending) {
                    // Il y a des mouvements en attente -> sélectionner le premier et aller sur retour
                    setSelectedMovement(pendingList[0]);
                    focusRetour();
                } else {
                    // Pas de mouvements en attente -> aller sur envoi
                    setSelectedMovement(null);
                    focusJoaillier();
                }
            } else {
                // Si shouldPopulate (après submit avec refresh), sélectionner le premier pending
                const pending = pendingList[0] || null;
                setSelectedMovement(pending);
                if (pending) {
                    focusRetour();
                } else {
                    focusJoaillier();
                }
            }
        },
        [focusJoaillier, focusRetour, normalizeMovements, ofContext, scheduleFocusOnOf]
    );

    const handleHistoriqueFetched = useCallback(
        (data, numSachet) => {
            console.log('🎯 handleHistoriqueFetched called with', data?.length, 'items');
            applyHistorique(data, { num_sachet: numSachet });
        },
        [applyHistorique]
    );

    const handleOfChange = useCallback((numSachet, _joaillierId, ofObject, options = {}) => {
        setOfContext({
            num_sachet: ofObject?.num_sachet || numSachet || null,
            ref: ofObject?.ref ?? null,
            refDesc: ofObject?.refDesc ?? null,
        });

        if (options.reset || !ofObject) {
            setHistoriqueData([]);
            // NE PAS vider draftMovements (SAISIES) - c'est l'historique de session
            setSelectedMovement(null);
            shouldPopulateDraftRef.current = false;
        }
    }, []);

    useEffect(() => {
        dispatchGetListeAtelier();
    }, [dispatchGetListeAtelier]);

    // Effect removed - focus logic now handled in applyHistorique

    const refreshHistorique = useCallback(
        async (contextOverride = {}) => {
            console.log('🔄 refreshHistorique called');
            if (!ofFormRef.current?.refetchHistorique) {
                return;
            }

            try {
                await ofFormRef.current.refetchHistorique(contextOverride.num_sachet);
                // Note: refetchHistorique appelle handleHistoriqueFetched qui appelle applyHistorique
                // Pas besoin d'appeler applyHistorique ici, ça causerait un double appel
            } catch (error) {
                console.error("Erreur lors du rafraichissement de l'historique :", error);
                showToast("Impossible de rafraichir l'historique", 'error');
            }
        },
        [showToast]
    );

    const handleEnvoiAjouter = useCallback(
        async (formData) => {
            if (!ofContext.num_sachet) {
                showToast('Sélectionnez un N° OF', 'warning');
                if (ofFormRef.current?.focusOfField) {
                    setTimeout(() => ofFormRef.current.focusOfField(), 150);
                }
                return;
            }

            const { joaillier, poids } = formData;
            const poidsValue = parseNumberInput(poids);

            if (!joaillier?.id) {
                showToast('Sélectionnez un joaillier', 'warning');
                focusJoaillier();
                return;
            }

            if (!Number.isFinite(poidsValue) || poidsValue <= 0) {
                showToast('Poids invalide', 'warning');
                return;
            }

            const payload = {
                id_ligne: `temp_${Date.now()}`,
                id_joaillier: joaillier.id,
                id_ouvrier: joaillier.id,
                label: joaillier.label,
                joaillier: joaillier.label,
                n_sachet: ofContext.num_sachet,
                of: ofContext.num_sachet,
                poids_d: poidsValue,
                poids: poidsValue,
                poids_r: 0,
                temps: 0,
                rendu: 0,
                pesee: 0,
                ref: ofContext.ref,
                refDesc: ofContext.refDesc,
                avatar: joaillier.avatar || null,
            };

            try {
                await ajouterJoaillier(payload);
                showToast('Mouvement enregistré', 'success');

                skipRetourFocusRef.current = true;
                shouldPopulateDraftRef.current = true;
                console.log('✅ Before refreshHistorique - shouldPopulate:', shouldPopulateDraftRef.current);
                await refreshHistorique({ num_sachet: ofContext.num_sachet });

                setTimeout(() => closeToast(), 2000);
            } catch (error) {
                if (error?.message === 'COMBO_ALREADY_IN_PROGRESS') {
                    showToast('La combinaison Joaillier / OF est déjà en cours', 'warning');
                } else {
                    console.error("Erreur lors de l'ajout du mouvement joaillier :", error);
                    showToast("Erreur lors de l'enregistrement", 'error');
                }
                skipRetourFocusRef.current = false;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [closeToast, focusJoaillier, ofContext, refreshHistorique, showToast]
    );

    const handleRetourAjouter = useCallback(
        async (formValues) => {
            if (!selectedMovement) {
                showToast('Sélectionnez un mouvement', 'warning');
                return;
            }

            closeToast();
            const poidsDepart = parseNumberInput(selectedMovement.poids_d ?? selectedMovement.poids);
            const poidsRetour = parseNumberInput(formValues.poidsR);
            const tempsValue = parseNumberInput(formValues.temps);

            if (!Number.isFinite(poidsDepart) || poidsDepart <= 0) {
                showToast('Poids départ invalide', 'warning');
                return;
            }

            if (!Number.isFinite(poidsRetour) || poidsRetour < 0) {
                showToast('Poids retour invalide', 'warning');
                return;
            }

            if (!Number.isFinite(tempsValue) || tempsValue < 0) {
                showToast('Temps invalide', 'warning');
                return;
            }

            const idLigne = selectedMovement.id_ligne || selectedMovement.id;

            if (!idLigne) {
                showToast('Identifiant du mouvement manquant', 'error');
                return;
            }

            const payload = {
                id_ligne: idLigne,
                of: selectedMovement.n_sachet,
                poids: poidsDepart,
                poidsR: poidsRetour,
                temps: tempsValue,
                rendu: 1,
                date_retour: getTodaySqlFormat(),
            };

            try {
                setLoadingRetour(true);
                await modifierJoaillier(payload);
                showToast('Retour enregistré', 'success');

                skipRetourFocusRef.current = true;
                shouldPopulateDraftRef.current = true;
                console.log('✅ Before refreshHistorique (retour) - shouldPopulate:', shouldPopulateDraftRef.current, 'num_sachet:', selectedMovement.n_sachet);
                await refreshHistorique({ num_sachet: selectedMovement.n_sachet });

                setTimeout(() => closeToast(), 2000);
            } catch (error) {
                console.error("Erreur lors de l'enregistrement du retour joaillier :", error);
                showToast("Erreur lors de l'enregistrement du retour", 'error');
                skipRetourFocusRef.current = false;
            } finally {
                setLoadingRetour(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [closeToast, refreshHistorique, selectedMovement, showToast]
    );

    const handleDeleteMovement = useCallback(
        (id_ligne) => {
            // Supprimer de l'historique (pas de la BD, juste local)
            setHistoriqueData((previous) => previous.filter((item) => item.id_ligne !== id_ligne));
            if (selectedMovement?.id_ligne === id_ligne) {
                setSelectedMovement(null);
            }
        },
        [selectedMovement]
    );

    const handleDeleteFromDraft = useCallback(
        (id_ligne) => {
            // Supprimer de SAISIES (historique session utilisateur)
            setDraftMovements((previous) => previous.filter((item) => item.id_ligne !== id_ligne));
        },
        []
    );

    const handleSelectMovement = useCallback((rowData) => {
        setSelectedMovement(rowData || null);
    }, []);

    const handleRefreshMovements = useCallback(async () => {
        skipRetourFocusRef.current = true;
        setSelectedMovement(null);

        const targetNumSachet = ofContext.num_sachet || null;

        if (!targetNumSachet) {
            scheduleFocusOnOf();
            return;
        }

        try {
            shouldPopulateDraftRef.current = true;
            await refreshHistorique({ num_sachet: targetNumSachet });
            dispatchGetListeAtelier();
        } finally {
            scheduleFocusOnOf();
        }
    }, [dispatchGetListeAtelier, ofContext, refreshHistorique, scheduleFocusOnOf]);

    const hasPendingMovement = useMemo(
        () => historiqueData.some((movement) => Number(movement.rendu) === 0),
        [historiqueData]
    );

    const selectedOfForForm = ofContext.num_sachet
        ? {
              num_sachet: ofContext.num_sachet,
              ref: ofContext.ref,
              refDesc: ofContext.refDesc,
          }
        : null;

    return (
        <div style={{ padding: '16px', backgroundColor: '#fafafa' }}>
            <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                    <Card elevation={3} style={{ borderRadius: '8px', marginBottom: '16px' }}>
                        <CardContent style={{ padding: '12px' }}>
                            <JoaillierEnvoiForm
                                ref={ofFormRef}
                                onSubmitForm={handleEnvoiAjouter}
                                onOfChange={handleOfChange}
                                onHistoriqueFetched={handleHistoriqueFetched}
                                isOfOnly
                            />
                        </CardContent>
                    </Card>

                    <Card elevation={3} style={{ borderRadius: '8px' }}>
                        <CardHeader
                            title="SAISIES"
                            titleTypographyProps={{ variant: 'h6', style: { fontWeight: 500 } }}
                            action={
                                draftMovements.length > 0 && (
                                    <Button
                                        variant="text"
                                        color="secondary"
                                        size="small"
                                        startIcon={<ClearIcon />}
                                        onClick={() => setDraftMovements([])}
                                        style={{ fontSize: '0.75rem' }}
                                    >
                                        Vider
                                    </Button>
                                )
                            }
                            style={{
                                backgroundColor: '#fff3e0',
                                padding: '12px 16px',
                                borderBottom: '1px solid #e0e0e0',
                            }}
                        />
                        <CardContent style={{ padding: '12px', maxHeight: '300px', overflowY: 'auto' }}>
                            <JoaillierMovements
                                data={[...draftMovements].reverse()}
                                onRowClick={handleSelectMovement}
                                onDelete={handleDeleteFromDraft}
                                minimal
                            />
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={9}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Card
                                elevation={3}
                                style={{ borderRadius: '8px', height: '280px', display: 'flex', flexDirection: 'column' }}
                            >
                                <CardHeader
                                    title="ENVOI"
                                    titleTypographyProps={{ variant: 'h6', style: { fontWeight: 500 } }}
                                    style={{
                                        backgroundColor: '#e3f2fd',
                                        padding: '12px 20px',
                                        borderBottom: '1px solid #e0e0e0',
                                    }}
                                />
                                <CardContent style={{ padding: '20px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                                    {!selectedOfForForm ? (
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                height: '100%',
                                                backgroundColor: '#fafafa',
                                                borderRadius: '8px',
                                                border: '1px dashed #e0e0e0',
                                                padding: '32px',
                                                textAlign: 'center',
                                            }}
                                        >
                                            <InfoOutlinedIcon style={{ fontSize: 48, color: '#bdbdbd', marginBottom: '16px' }} />
                                            <span style={{ color: '#757575', fontSize: '0.9rem' }}>
                                                Renseignez un N° OF
                                            </span>
                                        </div>
                                    ) : (
                                        <JoaillierEnvoiForm
                                            ref={envoiFormRef}
                                            onSubmitForm={handleEnvoiAjouter}
                                            selectedOf={selectedOfForForm}
                                            isFormOnly
                                            hideSubmitButton
                                        />
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Card
                                elevation={3}
                                style={{ borderRadius: '8px', height: '280px', display: 'flex', flexDirection: 'column' }}
                                data-retour-module
                            >
                                <CardHeader
                                    title="RETOUR"
                                    titleTypographyProps={{ variant: 'h6', style: { fontWeight: 500 } }}
                                    style={{
                                        backgroundColor: '#f3e5f5',
                                        padding: '12px 20px',
                                        borderBottom: '1px solid #e0e0e0',
                                    }}
                                />
                                <CardContent style={{ padding: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
                                    {hasPendingMovement && selectedMovement ? (
                                        <JoaillierRetourModule
                                            ref={retourFormRef}
                                            selectedItem={selectedMovement}
                                            onSubmit={handleRetourAjouter}
                                            loading={loadingRetour}
                                            hideSubmitButton
                                        />
                                    ) : (
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                height: '100%',
                                                backgroundColor: '#fafafa',
                                                borderRadius: '8px',
                                                border: '1px dashed #e0e0e0',
                                                padding: '32px',
                                                textAlign: 'center',
                                                margin: '12px',
                                            }}
                                        >
                                            <InfoOutlinedIcon style={{ fontSize: 48, color: '#bdbdbd', marginBottom: '16px' }} />
                                            <span style={{ color: '#757575', fontSize: '0.9rem' }}>
                                                Aucun retour en attente
                                            </span>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12}>
                            <Card elevation={3} style={{ borderRadius: '8px' }}>
                                <CardHeader
                                    title="HISTORIQUE"
                                    titleTypographyProps={{ variant: 'h6', style: { fontWeight: 500 } }}
                                    style={{
                                        backgroundColor: '#e3f2fd',
                                        padding: '12px 20px',
                                        borderBottom: '1px solid #e0e0e0',
                                    }}
                                />
                                <CardContent style={{ padding: '12px', width: '100%' }}>
                                    {historiqueData.length === 0 ? (
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor: '#fafafa',
                                                borderRadius: '8px',
                                                border: '1px dashed #e0e0e0',
                                                padding: '32px',
                                                textAlign: 'center',
                                                minHeight: '200px',
                                            }}
                                        >
                                            <InfoOutlinedIcon style={{ fontSize: 48, color: '#bdbdbd', marginBottom: '16px' }} />
                                            <span style={{ color: '#757575', fontSize: '0.9rem' }}>
                                                Renseignez un N° OF pour afficher l'historique
                                            </span>
                                        </div>
                                    ) : (
                                        <JoaillierMovements
                                            data={historiqueData}
                                            onRowClick={handleSelectMovement}
                                            onDelete={handleDeleteMovement}
                                            onRefresh={handleRefreshMovements}
                                            selectedMovement={selectedMovement}
                                            useListView
                                        />
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            <Snackbar
                open={!!alertMessage}
                autoHideDuration={3000}
                onClose={closeToast}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={closeToast} severity={alertSeverity} variant="filled">
                    {alertMessage}
                </Alert>
            </Snackbar>
        </div>
    );
};

const mapStateToProps = (state) => ({
    listeAtelier: state.atelier.listeAtelier || [],
});

const mapDispatchToProps = (dispatch) => ({
    dispatchGetListeAtelier: () => dispatch(getListeAtelierAction()),
});

export default connect(mapStateToProps, mapDispatchToProps)(EnvoiJoaillier);
